import React from 'react';
import { mutate } from 'swr';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { Flex } from '@ui-library/flex';
import { Form } from '@ui-library/form';
import { Button } from '@ui-library/button';
import { Modal } from '@ui-library/modalV2';
import useStores from '@stores/useStores';
import { ContextMenuV2 } from '@components/common/contextMenu';
import { useOAuth } from '@components/common/accountManagement/hooks/useOAuth';
import { canCreateAccount } from '@hooks/useActionGuard/utils/permissionChecks';
import { ApiGetResource } from '@api';

import {
  getResourceCacheKey,
  useLoadAccountsIntoAccountManagementStore,
} from './hooks/useLoadAccountsIntoAccountManagementStore';
import { AccountManagementCtx, AccountManagementStore, useAccountManagementStore } from './store';
import { AccountDetails } from './common/accountDetails';
import { CreateAccountModal } from './common/createAccountMultiStepModal';
import { SelectAccountModal } from './common/selectAccountMultiStepModal';
import { AccountManagementBase } from './common/accountManagementBase';
import { DestinationAuthSection } from './legacy/destOAuthSection';
import { DesignVariantProvider, useDesignVariant } from './designVariantContext';

const AccountManagementContent = styled(Flex)<{ variant?: 'next' }>`
  overflow: hidden;
  margin: ${({ variant }) => (variant ? '-1rem' : 0)};
  padding: ${({ variant }) => (variant ? '1rem' : 0)};
`;

const mapVariantToSupportedAccountTypes = {
  delivery: 'rudderAccountId',
  regulation: 'rudderDeleteAccountId',
} as const;

interface AccountManagementV1Props {
  initialAccountId?: string;
  accountId: string | undefined;
  disabled?: boolean;
  onComplete: (accountId: string) => void;
  onAccountReauthorize?: (accountId: string) => void;
  resourceDefinitionName: string;
}

// ts-prune-ignore-next
export const AccountManagementV1: React.FC<AccountManagementV1Props> = observer(
  ({
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
    resourceDefinitionName,
  }) => {
    const { workspaceStore } = useStores();
    const store = useAccountManagementStore();
    const variant = useDesignVariant();
    const isNext = variant === 'next';

    const { setReactionDisposer, compatibleAccounts, resourceType } = store;
    setReactionDisposer((accountId) => {
      onComplete(accountId);
      setShowCreateAccountModal(false);
      setShowSelectAccountModal(false);
    });

    const selectedAccountElement = <AccountDetails store={store} accountId={accountId} />;

    const [showCreateAccountModal, setShowCreateAccountModal] = React.useState(false);
    const [showSelectAccountModal, setShowSelectAccountModal] = React.useState(false);
    const [showReauthorizeAccountModal, setShowReauthorizeAccountModal] = React.useState(false);

    const { startReOAuth, loading } = useOAuth({
      onComplete: (accountId: string) => {
        mutate(
          getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
        ).finally(() => {
          store.setAccountId(accountId);
          onAccountReauthorize?.(accountId);
          setShowReauthorizeAccountModal(false);
        });
      },
    });

    const resetStoreAndShowModal = (showModal: (flag: boolean) => void) => {
      store.reset();
      showModal(true);
    };

    const createAccountPermission = canCreateAccount(resourceType);

    let actionElement = (
      <Button
        size="middle"
        variant={variant}
        level="secondary"
        onClick={() => resetStoreAndShowModal(setShowCreateAccountModal)}
        name="Create account"
        disabled={!createAccountPermission.isAllowed}
        tooltip={{ title: createAccountPermission.denialReason }}
      />
    );

    if (accountId) {
      actionElement = (
        <Button
          size="middle"
          level="secondary"
          variant={variant}
          onClick={() => resetStoreAndShowModal(setShowSelectAccountModal)}
          name="Switch"
        />
      );

      if (store.isAccountOAuth(accountId)) {
        actionElement = (
          <ContextMenuV2
            variant={variant}
            items={[
              {
                key: 'switch-account',
                label: 'Switch account',
                onClick: () => resetStoreAndShowModal(setShowSelectAccountModal),
              },
              {
                key: 'reauthorize-account',
                label: 'Re-authorize account',
                onClick: () => setShowReauthorizeAccountModal(true),
              },
            ]}
            size={isNext ? 'middle' : 'small'}
          />
        );
      }
    } else if (compatibleAccounts.length > 0) {
      actionElement = (
        <Button
          size="middle"
          level="secondary"
          variant={variant}
          onClick={() => resetStoreAndShowModal(setShowSelectAccountModal)}
          name="Select"
        />
      );
    }

    return (
      <>
        <CreateAccountModal
          open={showCreateAccountModal}
          onClose={() => setShowCreateAccountModal(false)}
        />
        <SelectAccountModal
          open={showSelectAccountModal}
          onClose={() => setShowSelectAccountModal(false)}
          selectedAccountId={accountId}
          initialAccountId={initialAccountId}
        />
        <Modal
          variant={variant}
          open={showReauthorizeAccountModal}
          title="Are you sure you want to re-authorize?"
          okText="Re-authorize account"
          okButtonProps={{ danger: true }}
          confirmLoading={loading}
          onOk={() => startReOAuth({ accountId })}
          onCancel={() => setShowReauthorizeAccountModal(false)}
        >
          Please be aware that scopes, access, and other settings made during re-authorization will
          affect this account's linked destinations.
        </Modal>
        <AccountManagementContent
          variant={variant}
          gap="0.5rem"
          alignItemsCenter
          spaceBetween
          flexGrow
        >
          {selectedAccountElement}
          {!disabled && actionElement}
        </AccountManagementContent>
      </>
    );
  },
);

type DestinationAccountManagementProps = AccountManagementV1Props & {
  title?: string;
  type?: 'regulation' | 'delivery';
};

const DestinationAccountManagement: React.FC<DestinationAccountManagementProps> = observer(
  ({
    resourceDefinitionName,
    title,
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
    type = 'delivery',
  }) => {
    const [form] = Form.useForm();
    const { destinationDefsListStore } = useStores();

    const destinationDefinition =
      destinationDefsListStore.getDestinationDefByName(resourceDefinitionName);

    const supportedAccountTypes =
      destinationDefinition?.config?.supportedAccountDefinitions?.[
        mapVariantToSupportedAccountTypes[type]
      ] || [];

    const destinationTypeSupportsAccountDefinitions = supportedAccountTypes.length > 0;
    const accountManagementStore = React.useMemo(
      () =>
        new AccountManagementStore(form, {
          resourceType: 'destination',
          resourceDefinitionName,
          supportedAccountTypes,
        }),
      [],
    );

    const { isLoading } = useLoadAccountsIntoAccountManagementStore({
      accountManagementStore,
      legacyAccountsRole:
        destinationDefinition!.config?.auth?.role || destinationDefinition!.name.toLowerCase(),
    });

    const newAccountManagementComponent = (
      <AccountManagementV1
        accountId={accountId || initialAccountId}
        initialAccountId={initialAccountId}
        disabled={disabled}
        onComplete={onComplete}
        onAccountReauthorize={onAccountReauthorize}
        resourceDefinitionName={resourceDefinitionName}
      />
    );

    const legacyAccountManagementComponent = (
      <DestinationAuthSection
        destinationDef={destinationDefinition!}
        initiallySelectedAccId={initialAccountId || ''}
        isDisabled={!!disabled}
        updateAccount={onComplete}
      />
    );

    const content = (() => {
      if (destinationTypeSupportsAccountDefinitions) {
        // Migration happens at the workspace level: all destinations of a type are migrated together.
        // If only legacy accounts exist (no new accounts), migration hasn't occurred yet.
        // For non-OAuth destinations, always render the new component regardless of migration status.
        if (
          accountManagementStore.compatibleAccounts.length === 0 &&
          accountManagementStore.legacyAccounts.length > 0
        ) {
          return legacyAccountManagementComponent;
        }

        return (
          <AccountManagementCtx.Provider value={accountManagementStore}>
            {newAccountManagementComponent}
          </AccountManagementCtx.Provider>
        );
      }

      return legacyAccountManagementComponent;
    })();

    return (
      <AccountManagementBase loading={isLoading} title={title}>
        {content}
      </AccountManagementBase>
    );
  },
);

type SourceAccountManagementProps = AccountManagementV1Props & {
  title?: string;
};

const SourceAccountManagement: React.FC<SourceAccountManagementProps> = observer(
  ({
    resourceDefinitionName,
    title,
    accountId = '',
    initialAccountId,
    disabled,
    onComplete,
    onAccountReauthorize,
  }) => {
    const [form] = Form.useForm();
    const { sourceDefinitionsListStore } = useStores();

    const sourceDefinition = sourceDefinitionsListStore.getByName(resourceDefinitionName);

    const supportedAccountTypes =
      sourceDefinition?.config?.supportedAccountDefinitions?.rudderAccountId || [];

    const accountManagementStore = React.useMemo(
      () =>
        new AccountManagementStore(form, {
          resourceType: 'source',
          resourceDefinitionName,
          supportedAccountTypes,
        }),
      [],
    );

    const { isLoading } = useLoadAccountsIntoAccountManagementStore({
      accountManagementStore,
    });

    if (supportedAccountTypes.length === 0) {
      return null;
    }

    return (
      <AccountManagementBase loading={isLoading} title={title}>
        <AccountManagementCtx.Provider value={accountManagementStore}>
          <AccountManagementV1
            accountId={accountId || initialAccountId}
            initialAccountId={initialAccountId}
            disabled={disabled}
            onComplete={onComplete}
            onAccountReauthorize={onAccountReauthorize}
            resourceDefinitionName={resourceDefinitionName}
          />
        </AccountManagementCtx.Provider>
      </AccountManagementBase>
    );
  },
);

type AccountManagementProps = (
  | (DestinationAccountManagementProps & { resourceType: 'destination' })
  | (SourceAccountManagementProps & { resourceType: 'source' })
) & { variant?: 'next' };

export const AccountManagement: React.FC<AccountManagementProps> = observer(
  ({ resourceType, variant, ...props }) => {
    const content =
      resourceType === 'destination' ? (
        <DestinationAccountManagement {...props} />
      ) : (
        <SourceAccountManagement {...props} />
      );

    return <DesignVariantProvider value={variant}>{content}</DesignVariantProvider>;
  },
);
